<script setup lang="ts">
import { ref } from "vue";
import UiChangesScreenMap from "./UiChangesScreenMap.vue";
import { UI_ZONES, zoneNumber, type UiZone } from "./uiChangesZones";

/**
 * Étape 2 : l'écran en un coup d'œil. Le schéma et la liste des zones se
 * répondent ; l'explication est celle de la zone choisie.
 */
const selected = ref<UiZone>("header");
</script>

<template>
  <div>
    <p class="ui-intro">{{ $t("canvas:uiChanges.screen.intro") }}</p>
    <div class="screen-step">
      <UiChangesScreenMap :selected="selected" @select="selected = $event" />
      <div>
        <div class="zone-list" role="tablist">
          <button
            v-for="zone in UI_ZONES"
            :key="zone"
            type="button"
            role="tab"
            class="zone-chip"
            :class="{ active: selected === zone }"
            :aria-selected="selected === zone"
            @click="selected = zone"
          >
            <span class="zone-number">{{ zoneNumber(zone) }}</span>
            {{ $t(`canvas:uiChanges.screen.zones.${zone}.title`) }}
          </button>
        </div>
        <p class="zone-text" role="tabpanel">
          {{ $t(`canvas:uiChanges.screen.zones.${selected}.text`) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-intro {
  margin: 0 0 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
}

// Schéma et explications côte à côte, l'un sous l'autre faute de place.
.screen-step {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 16px;
  align-items: start;
}

.zone-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.zone-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 10px 4px 5px;
  border: 1px solid var(--canvas-border);
  border-radius: 16px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  cursor: pointer;

  &.active {
    border-color: var(--canvas-btn-mode-border);
    background-color: var(--canvas-btn-mode-bg);
    color: #fff;
  }
}

.zone-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.85);
  color: #1a1a1a;
  font-size: 11px;
  font-weight: 700;

  .zone-chip.active & {
    background-color: var(--canvas-btn-active-bg);
  }
}

.zone-text {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.85);
}
</style>
